import React from 'react';

interface FormFieldProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Lets a caller tell a Tab out of the field from a click elsewhere. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** So a caller can put the focus back when the value is rejected. */
  inputRef?: React.Ref<HTMLInputElement>;
  placeholder?: string;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  iconButton?: React.ReactNode;
  /** Caps what can be typed, for a value with a fixed length. */
  maxLength?: number;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  onKeyDown,
  inputRef,
  placeholder,
  error,
  required = true,
  className = '',
  iconButton,
  maxLength,
}) => {
  return (
    <div className={`flex flex-col items-start gap-1 self-stretch ${className}`}>
      <label className="flex items-center self-stretch text-ds-text-primary text-xs h-lg:text-sm font-medium">
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </label>
      {/* Box and input, matching the SP app's FormField: a 12px radius on a
          bordered container the input sits transparently inside, rather than a
          border on the input itself. `rounded-ds-xl` is this repo's 12px token.
          The container also means the icon button is a flex sibling instead of
          an absolutely positioned overlay, so it can never land on the text.
          This field used to draw an 8px box filled with `bg-ds-bg` — the page
          grey — under a 2px focus halo, which matched nothing in either app. */}
      <div
        className={`flex w-full items-center self-stretch rounded-ds-xl border ${
          error
            ? 'border-error'
            : // `ds-dark-30` (rgba(39,39,74,0.302)), not `ds-border`. Measured against
              // the SP sign-up on develop: its form fields sit on rgb(184,190,201),
              // which is this token over white. `ds-border` is the lighter shell
              // border used for toolbars and cards, and at that weight the box
              // read as a different size next to SP's.
              'border-ds-dark-30 bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary'
        }`}
      >
        <input
          ref={inputRef}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          {...(maxLength !== undefined && { maxLength })}
          // Quaternary, one step lighter than the SP's tertiary: a hint that
          // reads as a hint rather than as a value already typed.
          // The padding lives on the input, not on the box. Google Places positions
          // its suggestion list against the input element, so padding on the wrapper
          // left the dropdown inset 13px from the field it belongs to.
          className="min-w-0 flex-1 border-none bg-transparent px-3 py-2 font-inter text-[14px] font-normal leading-5 text-ds-text-primary placeholder:text-ds-text-quaternary focus:outline-none focus:ring-0"
        />
        {iconButton && <div className="flex items-center pr-3">{iconButton}</div>}
      </div>
      {error && <span className="self-stretch text-xs text-error h-lg:text-sm">{error}</span>}
    </div>
  );
};

export default FormField;
